import { VILNIUS_TV_TOWER_COLLISION } from "./landmarks.ts";

export type AuthoredElevatorId = "vilnius-tv-tower-lift";
export type AuthoredElevatorStopId = "ground-lobby" | "observation-deck";

export interface AuthoredElevatorStop {
  readonly id: AuthoredElevatorStopId;
  readonly label: string;
  readonly levelLabel: string;
  readonly positionLocalM: Readonly<{ x: number; y: number; z: number }>;
  readonly facingYawLocalRadians: number;
  readonly interactionRadiusM: number;
  readonly interactionHeightM: number;
  readonly prompt: string;
}

export interface AuthoredElevatorDefinition {
  readonly id: AuthoredElevatorId;
  readonly label: string;
  readonly origin: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationYRadians: number;
  readonly travelDurationSeconds: number;
  readonly transferProgress01: number;
  readonly stops: readonly AuthoredElevatorStop[];
}

/**
 * PROVISIONAL - USER TUNING REQUIRED.
 *
 * The two stops are model-aligned safe standing points immediately outside
 * the lift doors. The short ride is intentionally compressed: it preserves
 * the sense of travelling up the 170 m tower without imposing a real-time
 * minute-long wait on every visit.
 */
export const AUTHORED_ELEVATORS: readonly AuthoredElevatorDefinition[] =
  Object.freeze([
    Object.freeze({
      id: "vilnius-tv-tower-lift",
      label: "Vilnius TV Tower lift",
      origin: VILNIUS_TV_TOWER_COLLISION.origin,
      rotationYRadians: VILNIUS_TV_TOWER_COLLISION.rotationYRadians,
      travelDurationSeconds: 2.8,
      transferProgress01: 0.52,
      stops: Object.freeze([
        Object.freeze({
          id: "ground-lobby",
          label: "Ground floor",
          levelLabel: "G",
          positionLocalM: { x: 0, y: 0.22, z: 20.65 },
          facingYawLocalRadians: Math.PI,
          interactionRadiusM: 2.75,
          interactionHeightM: 2.4,
          prompt: "Take lift to observation deck",
        }),
        Object.freeze({
          id: "observation-deck",
          label: "Observation deck",
          levelLabel: "67",
          positionLocalM: { x: 0, y: 166.38, z: 6.4 },
          facingYawLocalRadians: Math.PI,
          interactionRadiusM: 2.75,
          interactionHeightM: 2.4,
          prompt: "Take lift to ground floor",
        }),
      ]),
    }),
  ]);

export const VILNIUS_TV_TOWER_LIFT = AUTHORED_ELEVATORS[0]!;

export function authoredElevatorWorldPoint(
  elevator: Readonly<AuthoredElevatorDefinition>,
  local: Readonly<{ x: number; y: number; z: number }>,
): Readonly<{ x: number; y: number; z: number }> {
  const cosine = Math.cos(elevator.rotationYRadians);
  const sine = Math.sin(elevator.rotationYRadians);
  return {
    x: elevator.origin.x + local.x * cosine + local.z * sine,
    y: elevator.origin.y + local.y,
    z: elevator.origin.z - local.x * sine + local.z * cosine,
  };
}

export function authoredElevatorWorldStop(
  elevator: Readonly<AuthoredElevatorDefinition>,
  stop: Readonly<AuthoredElevatorStop>,
): Readonly<{
  x: number;
  y: number;
  z: number;
  facingYaw: number;
}> {
  return {
    ...authoredElevatorWorldPoint(elevator, stop.positionLocalM),
    facingYaw: elevator.rotationYRadians + stop.facingYawLocalRadians,
  };
}

export function isTvTowerObservationPosition(
  position: Readonly<{ x: number; y: number; z: number }>,
): boolean {
  const centre = authoredElevatorWorldPoint(VILNIUS_TV_TOWER_LIFT, {
    x: 0,
    y: 169.1,
    z: 0,
  });
  return Math.abs(position.y - centre.y) <= 5.2 &&
    Math.hypot(position.x - centre.x, position.z - centre.z) <= 20;
}
